import React, { useState } from 'react';
import { Trans } from '@lingui/macro';
import { IXlsxReportInput, exportReportXlsx } from '../../../helpers/exportXlsx';
import { exportReportPdf } from '../../../helpers/exportPdf';

interface IProps {
    buildInput: () => IXlsxReportInput;
    /** Render only the PDF button, hide XLSX (default: true) */
    pdfOnly?: boolean;
    /** Optional extra CSS classes on the wrapper div */
    className?: string;
}

/**
 * Export button row:
 *  • Left button  — PDF  (opens a styled HTML page → browser print/save as PDF)
 *  • Right button — XLSX (downloads an ExcelJS workbook)
 *
 * When pdfOnly=true only the PDF button is rendered (useful if exceljs is
 * not available in the current build).
 */
const ExportButtons = ({ buildInput, pdfOnly = true, className }: IProps) => {
    const [xlsxBusy, setXlsxBusy] = useState(false);
    const [pdfBusy,  setPdfBusy]  = useState(false);

    const handlePdf = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (pdfBusy) return;
        setPdfBusy(true);
        try {
            await exportReportPdf(buildInput());
        } catch (err) {
            console.error('PDF export failed', err);
        } finally {
            setPdfBusy(false);
        }
    };

    const handleXlsx = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (xlsxBusy) return;
        setXlsxBusy(true);
        try {
            await exportReportXlsx(buildInput());
        } catch (err) {
            console.error('XLSX export failed', err);
        } finally {
            setXlsxBusy(false);
        }
    };

    return (
        <div
            className={`export-btn-row${className ? ' ' + className : ''}`}
            style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
            {/* PDF button */}
            <a
                href="/"
                title="Exportovať do PDF"
                className="btn_action text_w500"
                style={{ opacity: pdfBusy ? 0.6 : 1, cursor: pdfBusy ? 'wait' : undefined }}
                onClick={handlePdf}
            >
                {pdfBusy ? (
                    <>
                        <svg
                            className="sico wh16"
                            style={{ animation: 'spin 1s linear infinite' }}
                        >
                            <use xlinkHref="/assets/layout/icons.svg#s_loader" />
                        </svg>
                        <span><Trans>Generujem…</Trans></span>
                    </>
                ) : (
                    <>
                        <svg className="sico wh16">
                            <use xlinkHref="/assets/layout/icons.svg#s_dl" />
                        </svg>
                        <span>PDF</span>
                    </>
                )}
            </a>

            {/* XLSX button (hidden in pdfOnly mode) */}
            {!pdfOnly && (
                <a
                    href="/"
                    title="Exportovať do XLSX"
                    className="btn_action text_w500"
                    style={{ opacity: xlsxBusy ? 0.6 : 1, cursor: xlsxBusy ? 'wait' : undefined }}
                    onClick={handleXlsx}
                >
                    {xlsxBusy ? (
                        <>
                            <svg
                                className="sico wh16"
                                style={{ animation: 'spin 1s linear infinite' }}
                            >
                                <use xlinkHref="/assets/layout/icons.svg#s_loader" />
                            </svg>
                            <span><Trans>Exportujem…</Trans></span>
                        </>
                    ) : (
                        <>
                            <svg className="sico wh16">
                                <use xlinkHref="/assets/layout/icons.svg#s_dl" />
                            </svg>
                            <span>XLSX</span>
                        </>
                    )}
                </a>
            )}
        </div>
    );
};

export default ExportButtons;
